import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
    ArrowRight,
    CheckCircle,
    Minus,
    Plus,
} from "lucide-react";
import {
    Plan,
    plans,
    getPricePerSeat,
    getPricingTierInfo,
    calculateMonthlyTotal,
    calculateYearlyTotal,
    calculateTotalPrice
} from "../lib/pricing";

type SelectedPlan = {
    plan: Plan;
    seats: number;
    billingCycle: "monthly" | "yearly";
    totalPrice: number;
};

export default function PricingPlans(
    { 
        setSelectedPlan,
        setShowModal
    }:{
      setSelectedPlan: (plan: SelectedPlan) => void;
      setShowModal: (value: boolean) => void
}) {
    const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");

    // 👇 independent seat counts per plan
    const [seatCounts, setSeatCounts] = useState<Record<string, number>>(
        Object.fromEntries(plans.map((p) => [p.id, 20]))
    );

    const updateSeatCount = (id: string, value: number) => {
        setSeatCounts((prev) => ({ ...prev, [id]: value }));
    };

    return (
        <div>
            {/* Billing toggle */}
            <div className="flex justify-center mb-8">
                <div className="bg-gray-100 rounded-lg p-1 flex space-x-2">
                    <button
                        onClick={() => setBillingCycle("monthly")}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${billingCycle === "monthly"
                                ? "bg-white shadow text-gray-900"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingCycle("yearly")}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${billingCycle === "yearly"
                                ? "bg-white shadow text-gray-900"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        Yearly
                    </button>
                </div>
            </div>

            {/* Plans grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
                {plans.map((plan) => {
                    const seatCount = seatCounts[plan.id];
                    const dynamicPricePerSeat = getPricePerSeat(plan.id, seatCount);
                    const monthlyTotal = calculateMonthlyTotal(plan.id, seatCount);
                    const yearlyTotal = calculateYearlyTotal(plan.id, seatCount);

                    return (
                        <Card
                            key={plan.id}
                            className={`relative overflow-hidden border-2 border-${plan.color}-200 hover:border-${plan.color}-400 transition-all duration-300 hover:shadow-xl`}
                        >
                            <div
                                className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${plan.color}-600 to-${plan.color}-500`}
                            ></div>
                            {plan.highlight && (
                                <Badge
                                    className={`absolute top-4 right-4 bg-${plan.color}-100 text-${plan.color}-800`}
                                >
                                    {plan.highlight}
                                </Badge>
                            )}

                            <CardContent className="p-8">
                                <div className="text-center mb-8">
                                    <div
                                        className={`w-16 h-16 bg-${plan.color}-100 rounded-full flex items-center justify-center mb-4 mx-auto`}
                                    >
                                        {plan.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                        {plan.title}
                                    </h3>
                                    <p className="text-gray-600 mb-6">{plan.description}</p>

                                    {/* Seat counter */}
                                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                        <div className="flex items-center justify-center mb-4">
                                            <button
                                                onClick={() =>
                                                    updateSeatCount(plan.id, Math.max(1, seatCount - 1))
                                                }
                                                className={`w-10 h-10 rounded-full bg-white border-2 border-${plan.color}-200 hover:border-${plan.color}-400 flex items-center justify-center transition-colors`}
                                            >
                                                <Minus className={`w-4 h-4 text-${plan.color}-600`} />
                                            </button>
                                            <div className="mx-4 text-center">
                                                <input
                                                    type="number"
                                                    value={seatCount}
                                                    onChange={(e) =>
                                                        updateSeatCount(plan.id, Math.max(1, Number(e.target.value)))
                                                    }
                                                    className={`w-16 h-10 text-center text-lg font-bold border-2 border-${plan.color}-200 rounded-lg focus:border-${plan.color}-400 focus:outline-none`}
                                                    min={1}
                                                />
                                            </div>
                                            <button
                                                onClick={() => updateSeatCount(plan.id, seatCount + 1)}
                                                className={`w-10 h-10 rounded-full bg-white border-2 border-${plan.color}-200 hover:border-${plan.color}-400 flex items-center justify-center transition-colors`}
                                            >
                                                <Plus className={`w-4 h-4 text-${plan.color}-600`} />
                                            </button>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-600">
                                                    {seatCount} seats × ${dynamicPricePerSeat}
                                                </span>
                                                <span className="font-medium">
                                                    ${monthlyTotal}/month
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 text-center">
                                                {getPricingTierInfo(plan.id, seatCount)}
                                            </div>
                                            {plan.setupFee > 0 ? (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Setup fee (one-time)</span>
                                                    <span className="font-medium">${plan.setupFee}</span>
                                                </div>
                                            ) : (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Setup fee (one-time)</span>
                                                    <span className="font-medium">$0</span>
                                                </div>
                                            )}
                                            <div className="border-t pt-2">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Annual total</span>
                                                    <span className="font-medium">
                                                        ${yearlyTotal + plan.setupFee}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Price Display - Centered */}
                                    <div className="mb-6 text-center">
                                        <div className="text-3xl font-bold text-gray-900">
                                            {billingCycle === "monthly"
                                                ? `$${monthlyTotal}/month`
                                                : `$${yearlyTotal}/year`}
                                        </div>
                                        {plan.setupFee > 0 ? (
                                            <p className="text-sm text-gray-500 mt-1">
                                                + ${plan.setupFee} one-time setup fee
                                            </p>
                                        ) : (
                                            <p className="text-sm text-gray-500 mt-1">
                                                No setup fee
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Features */}
                                <div className="space-y-4 mb-8">
                                    {plan.features.map((f) => (
                                        <div key={f} className="flex items-center text-gray-700">
                                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                            <span>{f}</span>
                                        </div>
                                    ))}
                                </div>

                                <Button
                                    onClick={() => {
                                        const totalPrice = calculateTotalPrice(
                                            plan.id,
                                            seatCount,
                                            billingCycle,
                                            plan.setupFee
                                        );

                                        const modifiedPlan = {
                                            ...plan,
                                            pricePerSeat: dynamicPricePerSeat
                                        };

                                        setSelectedPlan({
                                            plan: modifiedPlan,
                                            seats: seatCount,
                                            billingCycle,
                                            totalPrice,
                                        });
                                        setShowModal(true)
                                    }}
                                    className={`w-full !bg-${plan.color}-600 hover:!bg-${plan.color}-700 text-white py-3 text-lg rounded-lg`}
                                >
                                    Get Started with {seatCount} seat{seatCount > 1 ? "s" : ""}
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}
